import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../services'
import { queryKeys } from '../lib/queryClient'
import type {
  ControlCapabilities,
  CreateCommandPayload,
  DownlinkCommand,
} from '../types/control'

/**
 * Whether/how this device can be controlled (drives rendering of the panel).
 * Returns { controllable: false } for users without control access, so callers
 * can simply not render the panel.
 */
export function useControlCapabilities(sigfoxId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.controlCapabilities(sigfoxId),
    queryFn: async () => (await api.getControlCapabilities(sigfoxId)) as ControlCapabilities,
    enabled: enabled && !!sigfoxId,
    staleTime: 10 * 60 * 1000, // capabilities rarely change
    gcTime: 30 * 60 * 1000,
    retry: 1,
  })
}

/**
 * Command history + in-flight state for the device. Polls on an interval because
 * confirmation arrives asynchronously via the device's next uplink (minutes to
 * hours later, depending on poll cadence).
 */
export function useDeviceCommands(sigfoxId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.deviceCommands(sigfoxId),
    queryFn: async () => (await api.getDeviceCommands(sigfoxId)) as DownlinkCommand[],
    enabled: enabled && !!sigfoxId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // surface confirmations without a manual refresh
  })
}

export function useCreateDeviceCommand(sigfoxId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateCommandPayload) =>
      (await api.createDeviceCommand(sigfoxId, payload)) as DownlinkCommand,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deviceCommands(sigfoxId) })
    },
  })
}

export function useCancelDeviceCommand(sigfoxId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (commandId: string) => api.cancelDeviceCommand(sigfoxId, commandId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deviceCommands(sigfoxId) })
    },
  })
}
